import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  PostingForm,
  PostingFormTrackingSection,
  EditPostingState,
  SetPostingFormTracking,
  EditPostingFormHeaders,
  EditPostingFormNames,
  EditPostingForms,
  PostingTypes
} from 'remarketing-angular-library';
import { EditPostingUtilsService } from './edit-posting-utils.service';

@Injectable({
  providedIn: 'root'
})
export class PostingFormTrackingService {

  constructor(private readonly store: Store,
    private readonly editPostingUtilsService: EditPostingUtilsService) { }

  /**
   * Initializes the object structure for New Posting form tracking on the Edit Posting state. The structure is used to
   * track when a user has viewed necessary posting forms.
   */
  initializeNewPostingFormTrackingOnState(selectedForm: EditPostingForms) {
    const navigationHeaders = Object.values(EditPostingFormHeaders);
    const postingFormTrackingSections = navigationHeaders.map(header => this.initializePostingFormTrackingSection(header));
    this.handleFormBuildingForPostingSections(postingFormTrackingSections);
    this.setCurrentFormAsViewed(postingFormTrackingSections, selectedForm);
    this.store.dispatch(new SetPostingFormTracking(postingFormTrackingSections));
  }

  /**
   * Determines which forms belong within each PostingFormSection and sets them. A PostingFormSection aligns
   * with the navigation headers on the Edit Posting navigation. Each section contains forms.
   */
  handleFormBuildingForPostingSections(postingFormTrackingSections: PostingFormTrackingSection[]): void {
    const formsByHeaderMap = this.groupFormsByPostingFormSection();
    const headerTranslationMap = this.buildEditPostingNavigationHeaderTranslationMap();
    postingFormTrackingSections.forEach(postingFormSection => {
      this.addCorrectFormsAndTranslationKeyToPostingFormTrackingSection(postingFormSection, formsByHeaderMap, headerTranslationMap);
    });
  }

  /**
   * Adds the correct forms to the PostingFormTrackingSection along with the translationKey
   */
  addCorrectFormsAndTranslationKeyToPostingFormTrackingSection(section, formsByHeaderMap, headerTranslationMap): void {
    const formsWithinSection = formsByHeaderMap.get(section.header);
    const formsTranslationMap = this.buildEditPostingFormsTranslationMap();
    const formDefaultValidityMap = this.buildFormsDefaultValidityMap();

    const trackedFormsForSection = formsWithinSection.map(formIdentifier => this.initializePostingForm(formIdentifier, formsTranslationMap, formDefaultValidityMap));
    section.forms = trackedFormsForSection;
    section.translationKey = headerTranslationMap.get(section.header);
  }

  /**
 * Builds an initial PostingFormTrackingSection using the Edit Posting navigation header value and default values
 */
  initializePostingFormTrackingSection(header: string): PostingFormTrackingSection {
    const defaultDisplayValue = true;
    const defaultFormsInSection = [];

    return {
      header: header,
      forms: defaultFormsInSection,
      displayInFormTrackingModal: defaultDisplayValue
    } as PostingFormTrackingSection;
  }

  /**
   * Builds a PostingForm with form enum identifier, translation key, and default viewed value
   */
  initializePostingForm(identifier: string, formsTranslationMap, formDefaultIsValidValueMap) {
    const defaultViewedStatus = false;

    return {
      enumIdentifier: identifier,
      viewed: defaultViewedStatus,
      translationKey: formsTranslationMap.get(identifier),
      isValid: formDefaultIsValidValueMap.get(identifier)
    } as PostingForm;
  }

  /**
   * Groups forms according to which header they fall under on the edit posting navigation
   */
  groupFormsByPostingFormSection() {
    const pagesByHeader = new Map();

    pagesByHeader.set(EditPostingFormHeaders.VEHICLE_INFORMATION, [
      EditPostingFormNames.INFO_DESCRIPTION,EditPostingFormNames.INFO_LOCATION]);

    pagesByHeader.set(EditPostingFormHeaders.OPTIONS_AND_ACCESSORIES, [
      EditPostingFormNames.OPTIONS_EXTERIOR, EditPostingFormNames.OPTIONS_INTERIOR,
      EditPostingFormNames.OPTIONS_MECHANICAL, EditPostingFormNames.OPTIONS_ADDITIONAL
    ]);

    pagesByHeader.set(EditPostingFormHeaders.VEHICLE_IMAGES, [
      EditPostingFormNames.IMAGES_PHOTOS
    ]);

    pagesByHeader.set(EditPostingFormHeaders.VEHICLE_DISCLOSURES, [
      EditPostingFormNames.DISCLOSURES_CONDITION, EditPostingFormNames.DISCLOSURES_DAMAGES,
      EditPostingFormNames.DISCLOSURES_TIRES, EditPostingFormNames.DISCLOSURES_ADDITIONAL
    ]);

    pagesByHeader.set(EditPostingFormHeaders.VEHICLE_PRICING, [
      EditPostingFormNames.PRICING_PRICING_AND_FEES
    ]);

    return pagesByHeader;
  }

  /**
 * Sets the currently viewed form as viewed on the form tracking data
 */
  setCurrentFormAsViewed(postingFormTrackingSections: PostingFormTrackingSection[], selectedForm: EditPostingForms) {
    postingFormTrackingSections.forEach(postingFormTrackingSection => {
      postingFormTrackingSection.forms.forEach(form => {
        if(form.enumIdentifier === EditPostingForms[selectedForm]) {
          form.viewed = true;
        }
      });
    });
  }

  /**
   * Maps the Edit Posting form navigation section header to it's translation key
   */
  buildEditPostingNavigationHeaderTranslationMap() {
    const headerTranslationMap = new Map();
    headerTranslationMap.set(EditPostingFormHeaders.VEHICLE_INFORMATION, 'sellModule.posting.editPostingNav.textVehicleInfo');
    headerTranslationMap.set(EditPostingFormHeaders.OPTIONS_AND_ACCESSORIES, 'sellModule.posting.editPostingNav.textOptionAndAccessories');
    headerTranslationMap.set(EditPostingFormHeaders.VEHICLE_IMAGES, 'sellModule.posting.editPostingNav.textVehicleImage');
    headerTranslationMap.set(EditPostingFormHeaders.VEHICLE_DISCLOSURES, 'sellModule.posting.editPostingNav.textVehicleDisclosures');
    headerTranslationMap.set(EditPostingFormHeaders.VEHICLE_PRICING, 'sellModule.posting.editPostingNav.textVehiclePricing');
    return headerTranslationMap;
  }

  /**
   * Maps the Edit Posting form enum value to it's translation key
   */
  buildEditPostingFormsTranslationMap() {
    const formTranslationMap = new Map();
    formTranslationMap.set(EditPostingFormNames.INFO_DESCRIPTION, 'sellModule.posting.editPostingNav.textBasicInfo');
    formTranslationMap.set(EditPostingFormNames.INFO_LOCATION, 'sellModule.posting.editPostingNav.taxtLocation');
    formTranslationMap.set(EditPostingFormNames.OPTIONS_EXTERIOR, 'sellModule.posting.editPostingNav.textExterior');
    formTranslationMap.set(EditPostingFormNames.OPTIONS_INTERIOR, 'sellModule.posting.editPostingNav.textInterior');
    formTranslationMap.set(EditPostingFormNames.OPTIONS_MECHANICAL, 'sellModule.posting.editPostingNav.textMechanical');
    formTranslationMap.set(EditPostingFormNames.OPTIONS_ADDITIONAL, 'sellModule.posting.editPostingNav.textFullDescription');
    formTranslationMap.set(EditPostingFormNames.IMAGES_PHOTOS, 'sellModule.posting.editPostingNav.textPhotos');
    formTranslationMap.set(EditPostingFormNames.DISCLOSURES_CONDITION, 'sellModule.posting.editPostingNav.textCondition');
    formTranslationMap.set(EditPostingFormNames.DISCLOSURES_DAMAGES, 'sellModule.posting.editPostingNav.textDamages');
    formTranslationMap.set(EditPostingFormNames.DISCLOSURES_TIRES, 'sellModule.posting.editPostingNav.textTires');
    formTranslationMap.set(EditPostingFormNames.DISCLOSURES_ADDITIONAL, 'sellModule.posting.editPostingNav.textAdditional');
    formTranslationMap.set(EditPostingFormNames.PRICING_PRICING_AND_FEES, 'sellModule.posting.editPostingNav.textPricingFees');
    return formTranslationMap;
  }

  buildFormsDefaultValidityMap() {
    const requiredFieldsInForm = false;
    const noRequiredFieldsInForm = true;
    const formDefaultIsValidValueMap = new Map();
    formDefaultIsValidValueMap.set(EditPostingFormNames.INFO_DESCRIPTION, requiredFieldsInForm);
    formDefaultIsValidValueMap.set(EditPostingFormNames.INFO_LOCATION, requiredFieldsInForm);
    formDefaultIsValidValueMap.set(EditPostingFormNames.OPTIONS_EXTERIOR, noRequiredFieldsInForm);
    formDefaultIsValidValueMap.set(EditPostingFormNames.OPTIONS_INTERIOR, noRequiredFieldsInForm);
    formDefaultIsValidValueMap.set(EditPostingFormNames.OPTIONS_MECHANICAL, noRequiredFieldsInForm);
    formDefaultIsValidValueMap.set(EditPostingFormNames.OPTIONS_ADDITIONAL, noRequiredFieldsInForm);
    formDefaultIsValidValueMap.set(EditPostingFormNames.IMAGES_PHOTOS, noRequiredFieldsInForm);
    formDefaultIsValidValueMap.set(EditPostingFormNames.DISCLOSURES_CONDITION, noRequiredFieldsInForm);
    formDefaultIsValidValueMap.set(EditPostingFormNames.DISCLOSURES_DAMAGES, noRequiredFieldsInForm);
    formDefaultIsValidValueMap.set(EditPostingFormNames.DISCLOSURES_TIRES, requiredFieldsInForm);
    formDefaultIsValidValueMap.set(EditPostingFormNames.DISCLOSURES_ADDITIONAL, noRequiredFieldsInForm);
    formDefaultIsValidValueMap.set(EditPostingFormNames.PRICING_PRICING_AND_FEES, requiredFieldsInForm);
    return formDefaultIsValidValueMap;
  }

  syncFormToInvalidValidationFormsOnLoad(postingType: PostingTypes, formName: EditPostingFormNames): void {
    if(PostingTypes.NEW === postingType && !this.formIsValid(formName)) {
      const defaultFormNotValid = false;
      this.editPostingUtilsService.addToValidationForms(defaultFormNotValid, formName);
    }
  }

  isNewPostingViewedAndValid(postingType: PostingTypes, formName: string): boolean {
    if(postingType === PostingTypes.NEW && this.formViewedAndValid(formName)) {
      return true;
    } else {
      return false;
    }
  }

  isNewPostingViewedButInvalid(postingType: PostingTypes, formName: string): boolean {
    if(postingType === PostingTypes.NEW && this.formViewedButInvalid(formName)) {
      return true;
    } else {
      return false;
    }
  }

  formViewedAndValid(formName: string) {
    return this.formIsViewed(formName) && this.formIsValid(formName);
  }

  formViewedButInvalid(formName: string) {
    return this.formIsViewed(formName) && !this.formIsValid(formName);
  }

  formIsViewed(formName: string) {
    const postingFormTracking = this.store.selectSnapshot(EditPostingState.postingFormTracking);

    let isViewed = false;
    postingFormTracking.forEach(postingSection => {
      postingSection.forms.forEach(form => {
        if (form.enumIdentifier === formName) {
          isViewed = form.viewed;
        }
      });
    });
    return isViewed;
  }

  formIsValid(formName: string): boolean {
    let isValid = false;

    const postingFormTracking = this.store.selectSnapshot(EditPostingState.postingFormTracking);
    postingFormTracking.forEach(postingSection => {
      postingSection.forms.forEach(form => {
        if (form.enumIdentifier === formName) {
          isValid = form.isValid;
        }
      });
    });
    return isValid;
  }
}
